import { Link } from "react-router-dom";
import type { Software } from "@/utils/software";
import { formatDownloads } from "@/utils/software";
import RatingStars from "./RatingStars";

interface Props {
  item: Software;
  layout?: "grid" | "list";
  onDownload?: (item: Software) => void;
}

export default function SoftwareCard({ item, layout = "grid", onDownload }: Props) {
  if (layout === "list") {
    return (
      <article className="card-surface group flex flex-col sm:flex-row gap-4 p-4 hover:border-primary-400 transition-colors">
        <Link
          to={`/detail/${item.id}`}
          className="relative block w-full sm:w-[200px] h-[140px] shrink-0 overflow-hidden rounded-md bg-secondary-900"
        >
          <img
            src={item.cover}
            alt={`${item.name}软件界面截图`}
            title={`${item.name} 黄金投资软件下载`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-2 top-2 chip bg-secondary-900/80 text-primary-200 backdrop-blur-sm">
            {item.categoryName}
          </span>
        </Link>
        <div className="flex flex-1 flex-col gap-3">
          <div>
            <Link
              to={`/detail/${item.id}`}
              className="font-heading text-lg font-bold text-foreground-900 transition-colors hover:text-primary-700"
            >
              {item.name}
            </Link>
            <p className="mt-1 line-clamp-2 text-sm text-foreground-600">{item.intro}</p>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-price-tag-3-line" />v{item.version}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-download-2-line" />
              {formatDownloads(item.downloads)}次下载
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-smartphone-line" />
              {item.platforms.join(" / ")}
            </span>
            <RatingStars rating={item.rating} />
          </div>
          <div className="mt-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => onDownload?.(item)}
              className="btn-accent text-sm py-2"
            >
              <i className="ri-download-2-line" />免费下载
            </button>
            <Link to={`/detail/${item.id}`} className="btn-ghost text-sm py-2">
              查看详情 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="card-surface group flex flex-col overflow-hidden hover:border-primary-400 transition-colors">
      <Link
        to={`/detail/${item.id}`}
        className="relative block h-[164px] w-full overflow-hidden bg-secondary-900"
      >
        <img
          src={item.cover}
          alt={`${item.name}软件界面截图`}
          title={`${item.name} 黄金投资软件下载`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-3 top-3 chip bg-secondary-900/80 text-primary-200 backdrop-blur-sm">
          {item.categoryName}
        </span>
        <span className="absolute right-3 top-3 chip bg-primary-500 text-foreground-950 font-semibold">
          免费
        </span>
      </Link>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div>
          <Link
            to={`/detail/${item.id}`}
            className="font-heading text-lg font-bold text-foreground-900 transition-colors hover:text-primary-700 line-clamp-1"
          >
            {item.name}
          </Link>
          <p className="mt-1 line-clamp-2 text-sm text-foreground-600">{item.intro}</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-price-tag-3-line" />v{item.version}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-download-2-line" />
            {formatDownloads(item.downloads)}
          </span>
          <RatingStars rating={item.rating} />
        </div>
        <div className="mt-auto flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={() => onDownload?.(item)}
            className="btn-primary flex-1 py-2 text-sm"
          >
            <i className="ri-download-2-line" />免费下载
          </button>
          <Link
            to={`/detail/${item.id}`}
            aria-label={`${item.name}详情`}
            className="btn-ghost px-3 py-2 text-sm"
          >
            <i className="ri-information-line" />
          </Link>
        </div>
      </div>
    </article>
  );
}